<template>
  <div class="w-full relative">
    <div class="absolute left-0 top-0 z-10 h-full w-full map-linear-gradient" />
    <img
      class="absolute left-0 top-0 h-full w-full object-cover object-center select-none"
      :src="`/maps/${matchData?.map_name}_cs2.png`"
    />

    <!-- Top Alert Bar (visible while match is processing) -->
    <div
      v-if="isProcessing"
      class="relative z-[60] w-full h-[38px] bg-[#c44cb640] border-b border-secondary-500 flex items-center px-4 lg:px-9 gap-4"
    >
      <div class="flex items-center gap-1.5 shrink-0">
        <span class="text-secondary-300 text-xs font-bold leading-[18px]">
          {{ $t('coach.match_header.processing_by_round') }}
        </span>
        <div
          class="relative flex items-center"
          @mouseenter="showProcessingTooltip = true"
          @mouseleave="showProcessingTooltip = false"
        >
          <InformationCircleIcon class="w-4 h-4 text-secondary-300 cursor-help" />
          <div
            v-if="showProcessingTooltip"
            class="absolute top-full left-1/2 -translate-x-1/2 mt-2 z-[60] w-[336px] p-3 bg-gray-800 rounded-lg shadow-lg pointer-events-none"
          >
            <div class="absolute -top-1 left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-800" />
            <p class="text-xs leading-[18px] text-text-secondary">
              {{ $t('coach.match_header.processing_tooltip') }}
            </p>
          </div>
        </div>
      </div>
      <div class="flex-1 max-w-[255px] h-1 bg-secondary-700 rounded-full overflow-hidden">
        <div
          class="h-full rounded-full processing-bar-fill transition-[width] duration-500 ease-out"
          :style="{ width: `${processingProgress}%` }"
        />
      </div>
    </div>

    <div class="w-full h-full px-4 lg:px-9 pt-6 flex flex-col">
      <div class="w-full grid grid-cols-3 gap-4 items-start">
        <!-- Platform + Map -->
        <div class="flex gap-2 items-center">
          <img class="w-3 lg:w-5 h-3 lg:h-5 z-50 select-none rounded-md" :src="platformLogo" />
          <span class="text-text-secondary z-50 text-xs lg:text-base">{{ matchTypeLabel }}</span>
          <span class="text-text-disabled z-50">•</span>
          <img class="w-3 lg:w-5 h-3 lg:h-5 z-50 select-none" :src="`/maps/map_icon_${matchData?.map_name}.svg`" />
          <span class="text-text-secondary z-50 text-xs lg:text-base">{{ friendlyMapName }}</span>
        </div>

        <!-- Score -->
        <div v-if="scoreInfo" class="flex flex-col gap-3 items-center justify-center py-6">
          <div class="flex gap-2">
            <div v-if="isProcessing" class="text-xs lg:text-sm rounded-md bg-[#3B82F633] text-blue-400 px-2 py-1 z-50">{{ $t('coach.match_header.processing') }}</div>
            <template v-else>
              <div v-if="didUserWin" class="text-xs lg:text-sm rounded-md bg-[#12928333] text-success-400 px-2 py-1 z-50">{{ $t('coach.match_header.victory') }}</div>
              <div v-else-if="isTied" class="text-xs lg:text-sm rounded-md bg-[#8B000033] text-danger-300 px-2 py-1 z-50">{{ $t('coach.match_header.tie') }}</div>
              <div v-else-if="didUserWin !== null" class="text-xs lg:text-sm rounded-md bg-[#8B000033] text-danger-300 px-2 py-1 z-50">{{ $t('coach.match_header.defeat') }}</div>
            </template>
            <div v-if="matchData?.invalid_match" class="flex gap-2 items-center text-xs lg:text-sm rounded-md bg-warning-900 bg-opacity-50 text-warning-400 px-2 py-1 z-50" :title="$t('coach.match_header.invalidated_title')">
              <ExclamationTriangleIcon class="w-4 h-4" />
              <span>{{ $t('coach.match_header.invalidated') }}</span>
            </div>
          </div>
          <div class="text-4xl lg:text-[48px] z-50 font-bold text-text-primary flex gap-2 items-center">
            <h1 :class="[scoreColor(0), { 'score-pulse': pulseA }]">{{ scoreInfo[0] }}</h1>
            -
            <h1 :class="[scoreColor(1), { 'score-pulse': pulseB }]">{{ scoreInfo[1] }}</h1>
          </div>
          <div class="text-text-secondary text-xs lg:text-sm z-50 whitespace-nowrap">
            {{ moment(matchData?.played_on).format('h:mm A, MMM Do, YYYY') }}
          </div>
        </div>

        <!-- Actions -->
        <div class="flex gap-2 items-center justify-end">
          <slot name="actions" />
        </div>
      </div>

      <!-- Tabs -->
      <div class="w-full">
        <nav class="w-full flex flex-row relative gap-9 border-b border-gray-800 z-50">
          <div
            v-for="tab in tabs"
            :key="tab.key"
            :ref="(el) => { if (el) tabRefs[tab.key] = el }"
            class="pb-4 text-center cursor-pointer flex gap-2 items-center w-full lg:w-auto justify-center lg:justify-start z-50"
            @click="$emit('updateTab', tab.key)"
          >
            <img :src="tab.icon" :class="[activeTab === tab.key ? 'brightness-200' : '']" class="h-5 w-5 duration-300" />
            <h3 class="leading-5 duration-300" :class="[activeTab === tab.key ? 'text-text-primary' : 'text-text-secondary']">
              {{ tab.label }}
            </h3>
          </div>
          <div class="absolute flex-row md:flex bottom-0 z-50">
            <div class="h-[2px] grow-transition" :style="{ width: `${underlinePadding}px` }" />
            <div class="h-[2px] bg-primary-600 grow-transition" :style="{ width: `${underlineWidth}px` }" />
          </div>
        </nav>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  ref, computed, watch, onMounted, onUnmounted, nextTick,
} from 'vue';
import { ExclamationTriangleIcon } from '@heroicons/vue/24/outline';
import { InformationCircleIcon } from '@heroicons/vue/20/solid';
import moment from 'moment';

const props = defineProps({
  matchData: Object,
  didUserWin: { type: Boolean, default: null },
  scoreInfo: Array,
  friendlyMapName: String,
  platformLogo: String,
  matchTypeLabel: String,
  activeTab: String,
  tabs: Array,
});

defineEmits(['updateTab']);

const isTied = computed(() => props.scoreInfo?.[0] === props.scoreInfo?.[1]);

const isProcessing = computed(() => {
  const status = props.matchData?.status;
  return status === 'queued_for_analysis' || status === 'analyzing' || status === 'processing';
});

const showProcessingTooltip = ref(false);

const processingProgress = computed(() => {
  const a = props.matchData?.a_score || 0;
  const b = props.matchData?.b_score || 0;
  const total = props.matchData?.total_rounds || 24;
  return Math.min(100, Math.max(0, Math.round(((a + b) / total) * 100)));
});

const pulseA = ref(false);
const pulseB = ref(false);
let pulseTimeoutA; let
  pulseTimeoutB;

function triggerPulse(which) {
  if (which === 0) {
    pulseA.value = false;
    nextTick(() => { pulseA.value = true; });
    clearTimeout(pulseTimeoutA);
    pulseTimeoutA = setTimeout(() => { pulseA.value = false; }, 500);
  } else {
    pulseB.value = false;
    nextTick(() => { pulseB.value = true; });
    clearTimeout(pulseTimeoutB);
    pulseTimeoutB = setTimeout(() => { pulseB.value = false; }, 500);
  }
}

watch(() => props.scoreInfo?.[0], (newVal, oldVal) => {
  if (oldVal !== undefined && newVal !== oldVal) triggerPulse(0);
});
watch(() => props.scoreInfo?.[1], (newVal, oldVal) => {
  if (oldVal !== undefined && newVal !== oldVal) triggerPulse(1);
});

function scoreColor(index) {
  if (props.didUserWin === null) return '';
  const isHigher = props.scoreInfo[index] > props.scoreInfo[1 - index];
  if (props.didUserWin) return isHigher ? 'text-success-400' : '';
  return isHigher ? 'text-danger-400' : '';
}

const tabRefs = ref({});
const underlineWidth = ref(92);
const underlinePadding = ref(0);

function updateUnderline() {
  nextTick(() => {
    const el = tabRefs.value[props.activeTab];
    if (el) {
      underlineWidth.value = el.offsetWidth;
      underlinePadding.value = el.offsetLeft;
    }
  });
}

watch(() => props.activeTab, updateUnderline);

let resizeTimeout;
function handleResize() {
  clearTimeout(resizeTimeout);
  resizeTimeout = setTimeout(updateUnderline, 100);
}

onMounted(() => {
  updateUnderline();
  window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
  clearTimeout(resizeTimeout);
});
</script>

<style scoped>
.map-linear-gradient {
  background: linear-gradient(180deg, #080a0d 0%, rgba(8, 10, 13, 0.8) 50%, #080a0d 100%);
}

@keyframes score-pulse {
  0%   { transform: scale(1); }
  35%  { transform: scale(1.25); }
  100% { transform: scale(1); }
}
.score-pulse {
  display: inline-block;
  animation: score-pulse 0.45s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes processing-shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
.processing-bar-fill {
  background-image: linear-gradient(90deg, #C44CB6 0%, #F4DCF1 50%, #C44CB6 100%);
  background-size: 200% 100%;
  animation: processing-shimmer 1.6s linear infinite;
}
</style>
